import * as THREE from 'three'
import gsap from 'gsap'
import Producer from '../Producer.js'
import vertexShader from '../shaders/square/vertex.glsl'
import fragmentShader from '../shaders/square/fragment.glsl'
import { DURATION, EASE } from '../../shared/motion.js'
import { disposeMaterial } from '../utils/dispose.js'

// ─────────────────────────────────────────────────────────────
// Shared Assets & Geometry (Memory Optimization)
// ─────────────────────────────────────────────────────────────

let idCounter = 0

/**
 * 共用 3D 幾何體：X 軸分為 32 段，供 Vertex Shader 在 Arc 模式下進行平滑 3D 弧線彎曲
 */
const SHARED_GEOMETRY = new THREE.PlaneGeometry(1, 1, 32, 1)

/**
 * 快取預設 1x1 白色 DataTexture (避免重寫與重複創建記憶體)
 */
const DEFAULT_WHITE_TEXTURE = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1)
DEFAULT_WHITE_TEXTURE.needsUpdate = true

/**
 * 解析 Hex 顏色值並轉換為 Linear SRGBColorSpace 下對應的 RGB 浮點數 Vector
 * 直接賦值 r/g/b 避免 Three.js ColorManagement 進行額外 sRGB 轉換導致顏色偏暗
 */
function parseHexColor(hex) {
    const cleanHex = hex.replace('#', '').trim() || '888888'
    const n = parseInt(cleanHex, 16)
    return new THREE.Color(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255)
}

/** 取得 DOM CSS 變數並轉為 THREE.Color，取不到時退回 fallback */
function getCssColor(varName, fallback) {
    let hex = fallback
    if (typeof document !== 'undefined') {
        const cssVal = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
        if (cssVal) {
            hex = cssVal
        }
    }
    return parseHexColor(hex)
}

const PLACEHOLDER_COLOR = getCssColor('--color-gray', '#888888')
const PLACEHOLDER_BG_COLOR = getCssColor('--color-white', '#f7f7f7')

// ─────────────────────────────────────────────────────────────
// Square Class (WebGL Card Unit)
// ─────────────────────────────────────────────────────────────

export default class Square {
    constructor(x, y, z, hw, hh, project, index = 0) {
        this.producer = new Producer()
        this.scene = this.producer.scene
        this.interaction = this.producer.interaction

        this.project = project
        this.index = index
        this.id = `sq${idCounter++}`
        this.hw = hw
        this.hh = hh

        this._leaveDelay = null
        this._interactive = false

        this._init(x, y, z)
    }

    // ─────────────────────────────────────────────────────────────
    // Initialization & Mesh Creation
    // ─────────────────────────────────────────────────────────────

    _init(x, y, z) {
        this._createMesh(x, y, z)
        this._subscribe()
    }

    /** 建立獨立 ShaderMaterial 實例與 Mesh */
    _createMesh(x, y, z) {
        this.material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            side: THREE.DoubleSide,
            uniforms: {
                uScale: { value: new THREE.Vector2(this.hw * 2, this.hh * 2) },
                uBend: { value: 0.0 }, // 由 Carousel 設定 (0 = Plane, 1 = Arc)
                uBendRadius: { value: 1.0 },
                uOpacity: { value: 1.0 },
                uFade: { value: 1.0 },
                uDim: { value: 1.0 },
                uTexture: { value: DEFAULT_WHITE_TEXTURE },
                uTextureMix: { value: 1.0 }, // 1.0 = 灰階/預設，0.0 = 全彩
                uTextureAspect: { value: 1.0 },
                uImageScale: { value: 1.0 },
                uHasImage: { value: 0.0 }, // 0.0 時 Fragment Shader 渲染佔位符角括號
                uHasColor: { value: this.project?.color ? 1.0 : 0.0 }, // 1.0 時整片替換為 uPlaceholderBg，不畫括號/X
                uPlaceholderColor: { value: PLACEHOLDER_COLOR },
                uPlaceholderBg: { value: this.project?.color ? parseHexColor(this.project.color) : PLACEHOLDER_BG_COLOR }
            }
        })

        this.mesh = new THREE.Mesh(SHARED_GEOMETRY, this.material)
        this.mesh.scale.set(this.hw * 2, this.hh * 2, 1)
        this.mesh.position.set(x, y, z)

        this.scene.add(this.mesh)
    }

    /** 向 Interaction 系統註冊 Raycast 事件 */
    _subscribe() {
        this.interaction.register(this.mesh, this)
        this._interactive = true
    }

    // ─────────────────────────────────────────────────────────────
    // Animations & State Updates
    // ─────────────────────────────────────────────────────────────

    /** 觸發從灰階/預設狀態過渡至彩色透明度全開狀態 */
    setColor() {
        gsap.to(this.material.uniforms.uTextureMix, {
            value: 0.0,
            duration: DURATION.color,
            ease: EASE.hover,
            overwrite: true
        })

        gsap.to([this.material.uniforms.uOpacity, this.material.uniforms.uImageScale], {
            value: 1.0,
            duration: DURATION.color,
            ease: EASE.hover,
            overwrite: true
        })
    }

    /** 每幀同步 Scale 至 Shader (Carousel 驅動 Mesh 縮放時同步 uScale) */
    update() {
        this.material.uniforms.uScale.value.set(this.mesh.scale.x, this.mesh.scale.y)
    }

    /** 響應式調整卡片尺寸 */
    resize({ hw, hh }) {
        this.hw = hw
        this.hh = hh
        const w = hw * 2
        const h = hh * 2

        this.mesh.scale.set(w, h, 1)
        // 供 Shader Cover-Fit 運算貼圖裁切與對齊
        this.material.uniforms.uScale.value.set(w, h)
    }

    // ─────────────────────────────────────────────────────────────
    // Lifecycle & Resource Cleanup
    // ─────────────────────────────────────────────────────────────

    destroy() {
        // 解綁 Raycast 互動
        if (this._interactive) {
            this.interaction.unregister(this.mesh)
            this._interactive = false
        }

        // 殺掉該 Material Uniforms 上所有的 GSAP 動畫
        this._leaveDelay?.kill()
        this._leaveDelay = null

        gsap.killTweensOf(this.material.uniforms.uTextureMix)
        gsap.killTweensOf(this.material.uniforms.uOpacity)
        gsap.killTweensOf(this.material.uniforms.uImageScale)

        // 從 Scene 圖層中移除 Mesh 並釋放 ShaderMaterial
        if (this.mesh) {
            this.scene.remove(this.mesh)
        }

        if (this.material) {
            disposeMaterial(this.material)
        }
    }
}
